import { z } from 'zod';
import { ESTADOS_SUSCRIPCION } from './enums.js';

/**
 * Consola de plataforma — el padrón que está por encima de los tenants.
 *
 * Es un dominio aparte del de mantenimiento, y por eso vive en su propio archivo
 * con su propia matriz de permisos. Un `TipoAcceso` describe qué puede hacer
 * alguien DENTRO de una organización; un `RolOperador` describe qué puede hacer
 * sobre el conjunto de organizaciones. Mezclarlos sería exactamente el error que
 * el aislamiento entre tenants busca evitar.
 */

const uuid = z.string().uuid('Identificador inválido');
const texto = (max: number) => z.string().trim().min(1, 'Requerido').max(max);
const textoOpcional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

// ─── Roles de operador ────────────────────────────────────────────────────────

export const ROLES_OPERADOR = ['SUPERADMIN', 'SOPORTE'] as const;
export type RolOperador = (typeof ROLES_OPERADOR)[number];
export const ETIQUETA_ROL_OPERADOR: Record<RolOperador, string> = {
  SUPERADMIN: 'Administrador de plataforma',
  SOPORTE: 'Soporte (solo lectura)',
};

export const PERMISOS_OPERADOR = [
  'tenant:leer',
  'tenant:escribir',
  'plan:leer',
  'plan:escribir',
  'suscripcion:administrar',
  'facturacion:cerrar',
  'operador:leer',
  'operador:administrar',
  'version_app:leer',
  'version_app:escribir',
] as const;
export type PermisoOperador = (typeof PERMISOS_OPERADOR)[number];

/**
 * Soporte lee y no escribe.
 *
 * La cuenta que se usa a diario para responder un ticket no necesita poder
 * cancelar la suscripción de un cliente, y la mayoría de las consultas de
 * soporte son justamente eso: mirar. Reservar la escritura al superadministrador
 * hace que el error caro exija entrar con la otra cuenta.
 */
const PERMISOS_SOPORTE: readonly PermisoOperador[] = [
  'tenant:leer',
  'plan:leer',
  'operador:leer',
  'version_app:leer',
];

export const PERMISOS_POR_ROL_OPERADOR: Record<RolOperador, readonly PermisoOperador[]> = {
  SUPERADMIN: PERMISOS_OPERADOR,
  SOPORTE: PERMISOS_SOPORTE,
};

export function operadorPuede(rol: RolOperador, permiso: PermisoOperador): boolean {
  return PERMISOS_POR_ROL_OPERADOR[rol].includes(permiso);
}

// ─── Autenticación de operador ────────────────────────────────────────────────

/** Seis dígitos TOTP. Se acepta con espacios porque las apps los muestran así. */
export const codigoTotpSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s+/g, ''))
  .pipe(z.string().regex(/^[0-9]{6}$/, 'El código son 6 dígitos'));

export const credencialesOperadorSchema = z.object({
  email: z.string().trim().toLowerCase().email('Correo inválido'),
  contrasena: z.string().min(1, 'Requerido'),
  codigo: codigoTotpSchema.optional(),
});
export type CredencialesOperador = z.infer<typeof credencialesOperadorSchema>;

export const inscribirMfaSchema = z.object({
  email: z.string().trim().toLowerCase().email('Correo inválido'),
  contrasena: z.string().min(1, 'Requerido'),
});

export const confirmarMfaSchema = z.object({
  email: z.string().trim().toLowerCase().email('Correo inválido'),
  contrasena: z.string().min(1, 'Requerido'),
  codigo: codigoTotpSchema,
});

/**
 * Contraseña de operador: más exigente que la de un usuario de tenant.
 *
 * Estas credenciales ven el padrón completo y la facturación de todos los
 * clientes; el mínimo de 12 caracteres que se le pide a un técnico se queda
 * corto para la cuenta que puede cancelar una suscripción.
 */
export const contrasenaOperadorSchema = z
  .string()
  .min(16, 'Debe tener al menos 16 caracteres')
  .max(128, 'No puede exceder 128 caracteres')
  .refine((v) => /[a-z]/.test(v), 'Debe incluir una minúscula')
  .refine((v) => /[A-Z]/.test(v), 'Debe incluir una mayúscula')
  .refine((v) => /[0-9]/.test(v), 'Debe incluir un número')
  .refine((v) => /[^A-Za-z0-9]/.test(v), 'Debe incluir un símbolo');

export const cambiarContrasenaOperadorSchema = z.object({
  contrasenaActual: z.string().min(1, 'Requerido'),
  contrasenaNueva: contrasenaOperadorSchema,
});

export interface OperadorSesion {
  id: string;
  nombreCompleto: string;
  email: string;
  rol: RolOperador;
  permisos: PermisoOperador[];
}

/**
 * Respuesta del inicio de sesión cuando falta completar el segundo factor.
 *
 * `secreto` y `uri` solo viajan durante la inscripción, es decir, cuando la
 * cuenta todavía no tiene MFA confirmada. Una vez confirmada, el servidor jamás
 * los vuelve a emitir.
 */
export interface DesafioMfa {
  mfaPendiente: true;
  inscripcion: { secreto: string; uri: string } | null;
}

// ─── Operadores ───────────────────────────────────────────────────────────────

export const operadorSchema = z.object({
  email: z.string().trim().toLowerCase().email('Correo inválido').max(255),
  nombreCompleto: texto(150),
  rol: z.enum(ROLES_OPERADOR),
  contrasena: contrasenaOperadorSchema,
});

export const actualizarOperadorSchema = z.object({
  nombreCompleto: texto(150).optional(),
  rol: z.enum(ROLES_OPERADOR).optional(),
  activo: z.boolean().optional(),
});

export interface OperadorInfo {
  id: string;
  email: string;
  nombreCompleto: string;
  rol: RolOperador;
  activo: boolean;
  mfaConfirmada: boolean;
  ultimaVisita: string | null;
  creadoEl: string;
}

// ─── Tenants ──────────────────────────────────────────────────────────────────

export const slugTenantSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{1,49}$/, 'Solo minúsculas, números y guiones; entre 2 y 50 caracteres');

/** RUC peruano: once dígitos. Opcional, porque un cliente en prueba aún no lo da. */
export const rucSchema = z
  .string()
  .trim()
  .regex(/^[0-9]{11}$/, 'El RUC son 11 dígitos')
  .optional()
  .or(z.literal('').transform(() => undefined));

/**
 * Alta de organización.
 *
 * Incluye al primer administrador porque un tenant sin ninguna cuenta capaz de
 * entrar no sirve para nada, y crearlo en dos pasos deja la ventana en que
 * existe una organización a la que nadie puede acceder.
 */
export const crearTenantSchema = z.object({
  slug: slugTenantSchema,
  razonSocial: texto(200),
  ruc: rucSchema,
  planCodigo: texto(50),
  asientosContratados: z.number().int().min(1).max(10_000),
  estado: z.enum(['PRUEBA', 'ACTIVA']).default('PRUEBA'),
  administrador: z.object({
    nombreCompleto: texto(150),
    email: z.string().trim().toLowerCase().email('Correo inválido').max(255),
    contrasena: z.string().min(12, 'Debe tener al menos 12 caracteres').max(128),
  }),
});

export const actualizarTenantSchema = z.object({
  razonSocial: texto(200).optional(),
  ruc: rucSchema,
  zonaHoraria: textoOpcional(60),
  activo: z.boolean().optional(),
});

export interface TenantResumen {
  id: string;
  slug: string;
  razonSocial: string;
  ruc: string | null;
  activo: boolean;
  plan: string | null;
  planCodigo: string | null;
  estadoSuscripcion: string | null;
  asientosContratados: number | null;
  /**
   * Asientos facturables en uso. No es el total de cuentas: los perfiles
   * «Únicamente solicitante» son gratuitos y no aparecen aquí.
   */
  asientosConsumidos: number;
  precioAsientoMensualPen: string | null;
  totalMensualPen: string | null;
  creadoEl: string;
}

export interface TenantDetalle extends TenantResumen {
  zonaHoraria: string;
  periodoInicio: string | null;
  periodoFin: string | null;
  eventos: EventoLicenciaInfo[];
  cierres: CierreMensual[];
}

export interface EventoLicenciaInfo {
  tipo: string;
  detalle: Record<string, unknown>;
  asientosConsumidos: number | null;
  asientosContratados: number | null;
  fechaHora: string;
}

export interface CierreMensual {
  periodo: string;
  asientosPico: number;
  asientosContratados: number;
  precioAsientoMensualPen: string;
  importePen: string;
  cerradoEl: string;
}

// ─── Planes ───────────────────────────────────────────────────────────────────

export const planSchema = z.object({
  codigo: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9-]{1,49}$/, 'Solo minúsculas, números y guiones'),
  nombre: texto(100),
  descripcion: textoOpcional(1000),
  precioAsientoMensualPen: z.coerce.number().min(0).max(99_999),
  asientosIncluidos: z.number().int().min(0).max(10_000).default(1),
  /** Sin tope cuando es `null`: es lo que distingue al plan corporativo. */
  maxAsientos: z.number().int().min(1).max(100_000).nullable().default(null),
  activo: z.boolean().default(true),
});

export const actualizarPlanSchema = planSchema.omit({ codigo: true }).partial();

export interface PlanInfo {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  precioAsientoMensualPen: string;
  asientosIncluidos: number;
  maxAsientos: number | null;
  activo: boolean;
  tenantsSuscritos: number;
}

// ─── Suscripciones ────────────────────────────────────────────────────────────

export const cambiarSuscripcionSchema = z
  .object({
    planCodigo: texto(50).optional(),
    asientosContratados: z.number().int().min(1).max(10_000).optional(),
    estado: z.enum(ESTADOS_SUSCRIPCION).optional(),
    motivo: textoOpcional(500),
  })
  .refine(
    (v) => v.planCodigo !== undefined || v.asientosContratados !== undefined || v.estado !== undefined,
    'Indique al menos un cambio',
  );

export const cerrarPeriodoSchema = z.object({
  /** Primer día del mes que se factura, en ISO. */
  periodo: z.string().regex(/^\d{4}-\d{2}(-\d{2})?$/, 'Use AAAA-MM'),
});

// ─── Panel ────────────────────────────────────────────────────────────────────

export interface ResumenPlataforma {
  tenantsActivos: number;
  tenantsSuspendidos: number;
  asientosContratados: number;
  asientosConsumidos: number;
  ingresoMensualRecurrentePen: string;
  intentosBloqueadosUltimos30Dias: number;
}

export interface EventoOperadorInfo {
  id: string;
  operadorEmail: string;
  accion: string;
  tenantSlug: string | null;
  detalle: Record<string, unknown>;
  ip: string | null;
  fechaHora: string;
}

// ─── Versión mínima de la app por plataforma ──────────────────────────────────

/**
 * Android e iOS son la app nativa (Capacitor); `web` cubre el navegador de
 * escritorio por si algún día conviene forzar un refresco allí también —hoy
 * ninguna fila de `web` bloquea nada porque el cliente web no consulta este
 * endpoint, pero la tabla ya la contempla para no migrar de nuevo si hiciera falta.
 * `tablet` es el APK cargado a mano (sideload, sin Play Store) en tablets
 * dedicadas — usa `urlDescarga` en vez de `urlTienda`.
 */
export const PLATAFORMAS_APP = ['android', 'ios', 'web', 'tablet'] as const;
export type PlataformaApp = (typeof PLATAFORMAS_APP)[number];

export interface VersionAppInfo {
  plataforma: PlataformaApp;
  /**
   * Piso absoluto: por debajo de esta versión, el bloqueo es SIEMPRE forzado,
   * sin importar `forzarActualizacion`. `null` = esta plataforma no exige
   * ninguna versión mínima.
   */
  versionMinima: string | null;
  /**
   * Último release publicado. Si la instalada quedó atrás pero sigue por
   * encima de `versionMinima`, el aviso lo decide `forzarActualizacion`.
   * `null` = no se está anunciando ninguna versión nueva.
   */
  versionActual: string | null;
  urlTienda: string | null;
  /** Link directo al archivo (APK sideload), en vez de a una ficha de tienda. */
  urlDescarga: string | null;
  /** Si el salto hasta `versionActual` (no el piso de `versionMinima`, ese siempre bloquea) es obligatorio. */
  forzarActualizacion: boolean;
  /** Bloquea la app sin importar la versión instalada: mantenimiento del backend. */
  modoMantenimiento: boolean;
  mensajeMantenimiento: string | null;
  actualizadoEl: string;
}

/**
 * Lo que responde `GET /auth/app-version`: el resultado ya evaluado, no los
 * datos crudos de la fila — el cliente no tiene que repetir la lógica de
 * comparación de versiones ni conocer `forzarActualizacion`.
 */
export interface EstadoVersionApp {
  mantenimiento: boolean;
  mensajeMantenimiento: string | null;
  actualizacionRequerida: boolean;
  /** Solo tiene sentido cuando `actualizacionRequerida` es `true`. */
  forzada: boolean;
  minima: string | null;
  actual: string | null;
  urlTienda: string | null;
  urlDescarga: string | null;
}

/**
 * Compara dos versiones `major.minor.patch` (sin sufijos de pre-release: la app
 * nativa nunca publica una versión con guion, así que no hace falta cubrirlo).
 * Devuelve negativo si `a < b`, positivo si `a > b`, 0 si son iguales.
 *
 * Un componente ausente cuenta como 0 (`'1.2'` vs `'1.2.0'` son iguales), y una
 * versión que no parsea como número se trata como 0 en vez de lanzar: un valor
 * mal escrito en la versión mínima guardada no debe tumbar la comparación, solo
 * comportarse como si esa versión fuera muy vieja.
 *
 * Vive en `shared` (no en la API) porque también la usa el script de
 * `packages/db` que sincroniza la versión mínima con la del build nativo.
 */
export function compararVersiones(a: string, b: string): number {
  const partesA = a.split('.');
  const partesB = b.split('.');
  const longitud = Math.max(partesA.length, partesB.length);

  for (let i = 0; i < longitud; i += 1) {
    const numeroA = Number.parseInt(partesA[i] ?? '0', 10) || 0;
    const numeroB = Number.parseInt(partesB[i] ?? '0', 10) || 0;
    if (numeroA !== numeroB) return numeroA - numeroB;
  }
  return 0;
}

const urlOpcional = (max: number) =>
  z
    .string()
    .trim()
    .url('URL inválida')
    .max(max)
    .optional()
    .or(z.literal('').transform(() => undefined));

export const actualizarVersionAppSchema = z.object({
  /** `versionName` en Android (`major.minor.patch`); vacío = sin mínimo, no bloquea a nadie. */
  versionMinima: textoOpcional(20),
  versionActual: textoOpcional(20),
  urlTienda: urlOpcional(500),
  urlDescarga: urlOpcional(1000),
  forzarActualizacion: z.boolean().default(false),
  modoMantenimiento: z.boolean().default(false),
  mensajeMantenimiento: textoOpcional(500),
});
export type ActualizarVersionApp = z.infer<typeof actualizarVersionAppSchema>;
